/** @module csi/table */
define([
	"dojo/query",
	"csi/constants",
	"csi/core/Key",
	"csi/html",
], function(query, constants, Key, html) {

	html.registerDataAttribute("DATA_TABLE_FREEZE_COLUMNS", "table-freeze-columns", Number, true);
	html.registerDataAttribute("DATA_TABLE_HEADER_INDEX", "table-header-index", Number, true);
	html.registerDataAttribute("DATA_TABLE_HEADER_MATRIX", "table-header-matrix", Boolean, true);
	html.registerDataAttribute("DATA_TABLE_LEVEL", "table-level", Number, true);
	html.registerDataAttribute("DATA_TABLE_MERGED", "table-merged", Boolean, true);
	html.registerDataAttribute("DATA_TABLE_RESULT", "table-result", Boolean, true);
	html.registerDataAttribute("DATA_TABLE_RESULT_LEVEL", "table-result-level", Number, true);
	html.registerDataAttribute("DATA_TABLE_SPEC_ID", "table-sid", null, true);
	html.registerDataAttribute("DATA_TABLE_SPAN_SPEC_ID", "table-span-sid", null, true);

	const TABLE_HEIGHTS = {};
	TABLE_HEIGHTS[constants.TABLE_HEIGHT_SMALL] = 175;
	TABLE_HEIGHTS[constants.TABLE_HEIGHT_MEDIUM] = 370;
	TABLE_HEIGHTS[constants.TABLE_HEIGHT_LARGE] = 570;

	const CSS_ACTION_COLUMN = "actionsColumn";
	const CSS_HEADER_CENTER = "headingCenter";
	const CSS_HEADER_CONTAINER = "csi-table-header-container";
	const CSS_HEADER_CONTENT = "csi-table-header-content";
	const CSS_HEADER_OVERFLOW = "csi-table-header-overflow";
	const CSS_HEADER_RIGHT = "headingRight";
	const CSS_HIERARCHY_ICON = "tableRowWidget";
	const CSS_MATRIX_2ND_DIMENSION = "csi-table-matrix-2nd-dimension";

	const SPANNER_TEXT = "__TextSpan__";
	const _cellClasses = [];

	/**
	 * Create colgroup for table, and assign width if provided
	 * @param {DOMNode} table
	 * @param {number|string} tableWidth
	 * @param {number[]|string[]} columnWidths
	 * @param {boolean} [bOverwrite]
	 */
	function fixTableWidths(table, tableWidth, columnWidths, bOverwrite) {
		const jTable = $(table);
		const jColGroup = jTable.find("> colgroup");
		const nColumns = columnWidths.length;
		var i;

		if (bOverwrite) {
			// for performance sake, overwrite widths as much as possible rather than replace with whole new colgroup
			const colgroup = jColGroup[0];
			const children = colgroup.childNodes;
			const nChildren = children.length;
			const min = Math.min(nColumns, nChildren);

			for (i = 0; i < min; ++i) {
				children[i].style.width = columnWidths[i] + html.PX;
			}

			if (nColumns > nChildren) {
				for (i; i < nColumns; ++i) {
					const jCol = $("<col/>");
					jCol.width(columnWidths[i]);
					jColGroup.append(jCol);
				}
			} else if (nChildren > nColumns) {
				for (i; i < nChildren; ++i) {
					colgroup.removeChild(colgroup.lastChild);
				}
			}
		} else {
			const jGroup = $("<colgroup/>");

			for (i = 0; i < nColumns; ++i) {
				const jCol = $("<col/>");
				jCol.width(columnWidths[i]);
				jGroup.append(jCol);
			}

			jColGroup.remove();
			jTable.prepend(jGroup);

			// handle case where width is either number (in pixels) or string (with em units); jQuery does not work here for some reason,
			// but does work with col nodes above
			if (typeof tableWidth === "string") {
				table.style.width = tableWidth;
				table.style.minWidth = tableWidth;
			} else {
				html.setPixels(table, html.WIDTH, tableWidth);
				html.setPixels(table, html.MINWIDTH, tableWidth);
			}
		}
	}

	/**
	 * Return row that is first of merged (spanned) rows
	 * @param {DOMNode} tr
	 * @return {DOMNode}
	 */
	function getBaseMergedRow(tr) {
		var trBase = tr;

		while (isMergedRow(trBase)) {
			trBase = trBase.previousSibling;
		}

		return (tr == trBase) ? null : trBase;
	}

	/**
	 * Return cell at given index taking into account colspan and rowspan;
	 * NOTE: only use for splitting print view - will not work with frozen tables
	 * @param {DOMNode} tr
	 * @param {number} n
	 * @param {Object} [params]
	 * @return {DOMNode}
	 */
	function getCellAtIndex(tr, n, params) {
		console.assert(n != null);

		var trBase = getBaseMergedRow(tr);
		var node, nodeBase;
		var i = 0;

		if (trBase) {
			// assume merged rows do not use colSpan
			nodeBase = trBase.firstChild;

			do {
				if (nodeBase.rowSpan == 1) {
					// if cell is not row-spanned, then advance cell on merged row as well
					node = node ? node.nextSibling : tr.firstChild;
				} else if (i == n) {
					// if cell at index is row-spanned, then it does not exist on merged row
					if (!params || !params.bReturnLastCell) {
						node = null;
					}
				}

				if (i++ == n) break;
				nodeBase = nodeBase.nextSibling;
			} while (true);
		} else {
			// non-merged rows may use colSpan
			node = tr.firstChild;

			while (i != n) {
				i += getCellColSpan(node);

				if (i > n) {
					if (params) params.bSpan = true;
					break;
				}

				if (node.nextSibling) {
					node = node.nextSibling;
				}
			}
		}

		return node;
	}

	/**
	 * Return table classes that can be applied to cells
	 * @return {string[]}
	 */
	function getCellClasses() {
		return _cellClasses;
	}

	/**
	 * Return colSpan of given cell
	 * @param {DOMNode} td
	 * @return {number}
	 */
	function getCellColSpan(td) {
		if (td) {
			return td.colSpan || 1;
		}

		return 0;
	}

	/**
	 * Return index of given cell in row; if merged row, then need to figure out based on base row
	 * @param {DOMNode} td
	 * @return {number}
	 */
	function getCellIndex(td) {
		var n = 0;
		var tr = td.parentNode;
		var trBase = getBaseMergedRow(tr);
		if (trBase) {
			// find index taking rowspan into account, but no colspan
			var tdBase = trBase.firstChild;
			var nSpan = tdBase.rowSpan;
			var tdMerge;

			do {
				// if cell is not row-spanned, then advance cell on merged row and test to see if it is the one
				if (tdBase.rowSpan != nSpan) {
					tdMerge = tdMerge ? tdMerge.nextSibling : tr.firstChild;
					if (tdMerge == td) return n;
				}

				tdBase = tdBase.nextSibling;
				++n;
			} while (tdBase);
		} else {
			// find index taking colspan into account, but no rowspan
			while (td = td.previousSibling) {
				n += getCellColSpan(td);
			}
		}

		return n;
	}

	/**
	 * Return Spec index of given header cell
	 * @param {DOMNode} th
	 * @erturn {number} 
	 */
	function getHeaderSpecIndex(th) {
		return html.getDataAttribute(th, html.DATA_TABLE_HEADER_INDEX);
	}

	/**
	 * Return element holding hierarchy icon
	 * @param {HTMLElement} element
	 * @return {HTMLElement}
	 */
	function getHierarchyElement(element) {
		return element.querySelector(`.${CSS_HIERARCHY_ICON}`);
	}

	/**
	 * Return list of rows that are merged (due to rowspan) with the given row. If bOmitRow is false, then the given row is included first.
	 * @param {DOMNode} row
	 * @param {boolean} [bOmitRow]
	 * @return {DOMNode[]}
	 */
	function getMergedRows(row, bOmitRow) {
		if (!row) {
			return [];
		}

		var rows = bOmitRow ? [] : [row];
		var r = row.nextSibling;

		while (isMergedRow(r)) {
			rows.push(r);
			r = r.nextSibling;
		}

		return rows;
	}

	/**
	 * Return minimum height for table
	 * @return {number}
	 */
	function getMinimumHeight() {
		return getFixedHeight(constants.TABLE_HEIGHT_SMALL);
	}

	/**
	 * Return number of columns to freeze in table
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getNumberOfFrozenColumns(domNode) {
		return html.getDataAttribute(domNode, html.DATA_TABLE_FREEZE_COLUMNS);
	}

	/**
	 * Return level of table where main (numbered) results are found
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getResultLevel(domNode) {
		return html.getDataAttribute(domNode, html.DATA_TABLE_RESULT_LEVEL);
	}

	/**
	 * Return rows of table body
	 * @param {DOMNode} tbody
	 * @return {NodeList}
	 */
	function getResultRows(tbody) {
		return query(">*", tbody);
	}

	/**
	 * Return hierarchy level of row
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getRowHierarchyLevel(row) {
		var hl = row ? html.getDataAttribute(row, html.DATA_TABLE_LEVEL) : -1;
		return hl || 0;
	}

	/**
	 * Return fixed table height
	 * @param {string} tableHeight - table height setting
	 * @return {string}
	 */
	function getFixedHeight(tableHeight) {
		return TABLE_HEIGHTS[tableHeight];
	}

	/**
	 * Return true if height setting is automatic height
	 * @param {string} value - height setting
	 * @return {boolean}
	 */
	function isAutomaticHeight(tableHeight) {
		return tableHeight == constants.TABLE_HEIGHT_AUTO;
	}

	/**
	 * Return true if header cell is for matrix
	 * @param {DOMNode} th
	 * @return {boolean}
	 */
	function isMatrixHeader(th) {
		return html.getDataAttribute(th, html.DATA_TABLE_HEADER_MATRIX);
	}

	/**
	 * Return true if row is merged with another
	 * @param {DOMNode} row
	 * @return {boolean}
	 */
	function isMergedRow(row) {
		return html.getDataAttribute(row, html.DATA_TABLE_MERGED);
	}

	/**
	 * Return true if row is merged with another
	 * @param {DOMNode} row
	 * @return {boolean}
	 */
	function isResultRow(row) {
		return html.getDataAttribute(row, html.DATA_TABLE_RESULT);
	}

	/**
	 * Return true if target class is derived from TableSpanner
	 * @param {Relationship} rel
	 * @return {boolean}
	 */
	function isTableSpannerRelationship(rel) {
		return rel && rel.m_rcTarget && rel.m_rcTarget.isDerivedFrom("TableSpanner");
	}

	/**
	 * Register CSS class that will be dynamically added to table cells
	 * @param {string} css
	 */
	function registerCellClass(css) {
		_cellClasses.push(css);
	}

	/**
	 * Determine if key at given index is a matrix group key
	 * @param {string[]} keys 
	 * @param {number} index 
	 * @return {boolean}
	 */
	function _isMatrixKey(keys, index) {
		// we don't have a good way to detect a matrix group, so instead look for matrix in next key
		const dpkey = keys[index + 1];
		const key = new Key({dpkey});
		return !!key.matrix;
	}

	/**
	 * Set number of columns to freeze in table
	 * @param {DOMNode} th
	 * @param {number} nIndex
	 */
	function setHeaderSpecIndex(th, nIndex) {
		html.setDataAttribute(th, html.DATA_TABLE_HEADER_INDEX, nIndex);
	}

	/**
	 * Set number of columns to freeze in table
	 * @param {DOMNode} th
	 */
	function setMatrixHeader(th) {
		html.setDataAttribute(th, html.DATA_TABLE_HEADER_MATRIX, true);
	}

	/**
	 * Set number of columns to freeze in table
	 * @param {DOMNode} domNode
	 * @param {number} nColumns
	 */
	function setNumberOfFrozenColumns(domNode, nColumns) {
		html.setDataAttribute(domNode, html.DATA_TABLE_FREEZE_COLUMNS, nColumns);
	}

	/**
	 * Set level of table at which results are found
	 * @param {DOMNode} domNode
	 * @param {number} nLevel
	 */
	function setResultLevel(domNode, nLevel) {
		html.setDataAttribute(domNode, html.DATA_TABLE_RESULT_LEVEL, nLevel);
	}

	/**
	 * Mark row as a result row
	 * @param {DOMNode} row
	 */
	function setRowAsResult(row) {
		html.setDataAttribute(row, html.DATA_TABLE_RESULT, true);
	}

	/**
	 * Set level of table at which results are found
	 * @param {DOMNode} domNode
	 * @param {number} nLevel
	 */
	function setRowHierarchyLevel(row, nLevel) {
		html.setDataAttribute(row, html.DATA_TABLE_LEVEL, nLevel);
	}

	/**
	 * Return width for scroll bar in table
	 * @return {number}
	 */
	function getTableScrollbarWidth() {
		// we used to have here " ||12" which would always return at least 12
		// however, scrollbar width may be zero, and it is valid, as Mac, and now Firefox, have floating scrollbar
		// so we have to return zero here, otherwise we may have bugs like C8-58126 and also 
		// in case of floating toolbar we don't need to allocate space for toolbar
		return html.getVerticalScrollbarWidth();
	}

	return {
		// HTML_HIERARCHY_STATE values
		HS_NONE: "",
		HS_PLUS: "+",
		HS_MINUS: "-",

		// padding for image cells - see TableImagePadding
		IMAGE_CELL_PADDING: 5,

		CSS_ACTION_COLUMN,
		CSS_HEADER_CENTER,
		CSS_HEADER_CONTAINER,
		CSS_HEADER_CONTENT,
		CSS_HEADER_OVERFLOW,
		CSS_HEADER_RIGHT,
		CSS_HIERARCHY_ICON,
		CSS_MATRIX_2ND_DIMENSION,
		SPANNER_TEXT,

		fixTableWidths,
		getBaseMergedRow,
		getCellAtIndex,
		getCellClasses,
		getCellIndex,
		getFixedHeight,
		getHeaderSpecIndex,
		getHierarchyElement,
		getMergedRows,
		getMinimumHeight,
		getNumberOfFrozenColumns,
		getResultLevel,
		getResultRows,
		getRowHierarchyLevel,
		getTableScrollbarWidth,
		isAutomaticHeight,
		isMatrixHeader,
		isMergedRow,
		isResultRow,
		isTableSpannerRelationship,
		registerCellClass,
		setHeaderSpecIndex,
		setMatrixHeader,
		setNumberOfFrozenColumns,
		setResultLevel,
		setRowAsResult,
		setRowHierarchyLevel
	};
});